/* eslint-disable react/jsx-no-useless-fragment */
import { ReactNode } from "react";
import { Link } from "react-router-dom";
import Avatar from "@mui/material/Avatar";
import Tooltip from "@mui/material/Tooltip";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { dateTimeFormat, formatSignedDollar, type SettlementCorrection } from "src/utils";
import { bgColorbyName } from "src/variable/randomColor";
import cellStyles from "./transactionCells.module.scss";

const EM_DASH = "---";

/* ============ EmptyCell ============ */

type EmptyCellProps = {
  value?: ReactNode;
};

export const EmptyCell = ({ value = EM_DASH }: EmptyCellProps) => <span className={cellStyles.cellEmpty}>{value}</span>;

/* ============ MerchantCell ============ */

type MerchantCellProps = {
  value?: string;
  fallback?: ReactNode;
};

export const MerchantCell = ({ value, fallback }: MerchantCellProps) => {
  const text = value?.trim();

  if (!text) return <>{fallback ?? <EmptyCell />}</>;

  return <span className={cellStyles.tableMerchent}>{text}</span>;
};

/* ============ DateStack ============ */

type DateStackProps = {
  value?: string;
  fallback?: ReactNode;
};

export const DateStack = ({ value, fallback }: DateStackProps) => {
  if (!value) return <>{fallback ?? <EmptyCell />}</>;

  const formatted = dateTimeFormat(value);

  return (
    <>
      <div className={cellStyles.tableDate}>{formatted?.date}</div>
      <div className={cellStyles.tableDate}>{formatted?.time}</div>
    </>
  );
};

/* ============ ApprovalNumber ============ */

type ApprovalNumberProps = {
  value?: string;
  /** Render a "****" mask prefix before the value (used for card PANs). */
  masked?: boolean;
  fallback?: ReactNode;
};

export const ApprovalNumber = ({ value, masked = false, fallback }: ApprovalNumberProps) => {
  if (!value) return <>{fallback ?? <EmptyCell />}</>;

  return (
    <div className={cellStyles.approvalNumber}>
      {masked && <span className={cellStyles.approvalNumberMask}>****</span>}
      {value}
    </div>
  );
};

/* ============ MemberCell ============ */

type MemberCellProps = {
  fullName?: string;
  email?: string;
  userId?: string;
  isAdmin?: boolean;
  /**
   * Optional pill rendered under the admin name (e.g. "System action").
   * Only takes effect when `isAdmin` is true; ignored for regular users so
   * their email keeps the subtitle slot.
   */
  adminBadge?: ReactNode;
  fallback?: ReactNode;
};

export const MemberCell = ({ fullName, email, userId, isAdmin, adminBadge, fallback }: MemberCellProps) => {
  if (!fullName && !email) return <>{fallback ?? <EmptyCell />}</>;

  const display = fullName || email || "";
  const initial = display.charAt(0).toUpperCase();

  const subtitle = isAdmin
    ? adminBadge && <span className={cellStyles.adminBadge}>{adminBadge}</span>
    : email && <div className={cellStyles.tableMemberEmail}>{email}</div>;

  const inner = (
    <>
      {isAdmin ? (
        <span
          className={cellStyles.memberAvatar}
          style={{
            background: "linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)",
            color: "#64748b",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center"
          }}
        >
          <AdminPanelSettingsOutlinedIcon fontSize="small" />
        </span>
      ) : (
        <Avatar aria-label="Participant" className={cellStyles.memberAvatar} sx={{ bgcolor: bgColorbyName(display) }}>
          {initial}
        </Avatar>
      )}
      <div className={cellStyles.tableMemberDetail}>
        <div className={cellStyles.tableMemberName}>{fullName || email}</div>
        {subtitle}
      </div>
    </>
  );

  return userId ? (
    <Link to={`/users/${userId}`} className={cellStyles.tableMember}>
      {inner}
    </Link>
  ) : (
    <div className={cellStyles.tableMember}>{inner}</div>
  );
};

/* ============ WalletCell ============ */

type WalletCellProps = {
  walletId?: string;
  name?: string;
  description?: string;
  fallback?: ReactNode;
};

export const WalletCell = ({ walletId, name, description, fallback }: WalletCellProps) => {
  if (!walletId) return <>{fallback ?? <EmptyCell />}</>;

  return (
    <div className={cellStyles.tableWallet}>
      <Link to={`/wallets/${walletId}`} className={cellStyles.tableWalletName}>
        {name || walletId}
      </Link>
      {description && <div className={cellStyles.tableWalletDesc}>{description}</div>}
    </div>
  );
};

/* ============ StatusInfoIcon ============ */

const settlementTooltip = (settlement: SettlementCorrection) => {
  const formattedDate = settlement.settledAt ? dateTimeFormat(settlement.settledAt)?.date : undefined;
  const settledOn = formattedDate === EM_DASH ? undefined : formattedDate;
  const returnedToWallet = settlement.delta > 0;

  return (
    <div className={cellStyles.settlementTooltip}>
      <div className={cellStyles.settlementTooltipTitle}>Settlement correction</div>

      <div className={cellStyles.settlementTooltipRow}>
        <span>Authorised</span>
        <span>{formatSignedDollar(settlement.authorisedAmount, 2)}</span>
      </div>
      <div className={cellStyles.settlementTooltipRow}>
        <span>Settled</span>
        <span>{formatSignedDollar(settlement.settledAmount, 2)}</span>
      </div>
      <div className={cellStyles.settlementTooltipRow}>
        <span>Difference</span>
        <span>
          {formatSignedDollar(settlement.delta, 2)}
          <span className={cellStyles.settlementTooltipDirection}>
            {returnedToWallet ? "returned to the wallet" : "taken from the wallet"}
          </span>
        </span>
      </div>
      {settledOn && (
        <div className={cellStyles.settlementTooltipRow}>
          <span>Settled on</span>
          <span>{settledOn}</span>
        </div>
      )}

      <div className={cellStyles.settlementTooltipNote}>
        The difference was posted to the wallet as a separate adjustment transaction, so the amount on this row is
        unchanged from what was authorised.
      </div>
    </div>
  );
};

export type ReversalTooltipDetails = {
  title: string;
  reason?: string;
  reversedAt?: string;
  reversedBy: string;
};

const reversalTooltip = (reversal: ReversalTooltipDetails) => {
  const formattedDate = reversal.reversedAt ? dateTimeFormat(reversal.reversedAt)?.datetime : undefined;
  const reversedOn = formattedDate === EM_DASH ? undefined : formattedDate;

  return (
    <div className={cellStyles.settlementTooltip}>
      <div className={cellStyles.settlementTooltipTitle}>{reversal.title}</div>

      <div className={cellStyles.settlementTooltipRow}>
        <span>By</span>
        <span>{reversal.reversedBy}</span>
      </div>
      {reversedOn && (
        <div className={cellStyles.settlementTooltipRow}>
          <span>On</span>
          <span>{reversedOn}</span>
        </div>
      )}

      <div className={cellStyles.settlementTooltipNote}>{reversal.reason || "No reason was recorded."}</div>
    </div>
  );
};

type ReversedBadgeProps = {
  label: string;
  reversal?: ReversalTooltipDetails | null;
};

/** Marks an original whose money came back via a separate credit row. */
export const ReversedBadge = ({ label, reversal }: ReversedBadgeProps) => (
  <span className={cellStyles.reversedBadge}>
    {label}
    {reversal && <StatusInfoIcon reversal={reversal} />}
  </span>
);

type StatusInfoIconProps = {
  /** Only ever set on a declined row; takes precedence because a decline never settles. */
  declineReason?: string;
  settlement?: SettlementCorrection | null;
  reversal?: ReversalTooltipDetails | null;
};

/**
 * The info affordance on a transaction status pill. Shared by both tables and the
 * mobile card so the decline reason and the settlement breakdown look identical.
 */
export const StatusInfoIcon = ({ declineReason, settlement, reversal }: StatusInfoIconProps) => {
  let title: ReactNode = declineReason || null;

  if (!title && reversal) title = reversalTooltip(reversal);
  if (!title && settlement) title = settlementTooltip(settlement);

  if (!title) return null;

  return (
    <Tooltip
      title={title}
      arrow
      placement="top"
      enterTouchDelay={0}
      componentsProps={{
        tooltip: {
          sx: {
            bgcolor: "#1e293b",
            fontSize: "13px",
            padding: "8px 12px",
            maxWidth: "290px"
          }
        }
      }}
    >
      <InfoOutlinedIcon className={cellStyles.statusInfoIcon} />
    </Tooltip>
  );
};

/* ============ Amount / Balance ============ */

type AmountLikeProps = {
  text: string;
  color?: string;
  /** Set only when the settled amount differs from the authorised `text`. */
  settlement?: SettlementCorrection | null;
  /** Legacy single-row reversals only — struck through and neutral, never red. */
  reversed?: boolean;
  /** Money coming back. Prefixed with `+` because the credit sits beside an identical debit. */
  credit?: boolean;
};

/** The `-> $9.50` marker appended after an authorised amount. Shared by the tables and the detail page. */
export const SettledValue = ({ settlement }: { settlement: SettlementCorrection }) => (
  <span className={cellStyles.settlementValue}>&rarr; {formatSignedDollar(settlement.settledAmount, 2)}</span>
);

export const AmountCell = ({ text, color, settlement, reversed, credit }: AmountLikeProps) => (
  <div
    className={`${cellStyles.tableAmount} ${reversed ? cellStyles.tableAmountReversed : ""}`}
    style={!reversed && color ? { color } : undefined}
  >
    {credit ? `+${text}` : text}
    {settlement && <SettledValue settlement={settlement} />}
  </div>
);

/** Subtitle in the transaction list header explaining the amount column's notations. */
export const TransactionTableLegend = () => (
  <div className={cellStyles.transactionTableLegend}>
    <span className={cellStyles.transactionTableLegendSample}>Authorised &rarr; Settled</span> shows a settlement
    adjustment. A reversed transaction stays on the list and is joined by its own{" "}
    <span className={cellStyles.transactionTableLegendSample}>+</span> credit row; the two net to zero.
  </div>
);

type BalanceCellProps = {
  value?: number | null;
  color?: string;
};

export const BalanceCell = ({ value, color }: BalanceCellProps) => (
  <div className={cellStyles.tableBalance} style={color ? { color } : undefined}>
    {value === undefined || value === null ? EM_DASH : formatSignedDollar(value)}
  </div>
);

/* ============ AccessTypePill ============ */

type AccessTypePillProps = {
  accessType?: string;
  fallback?: ReactNode;
};

export const AccessTypePill = ({ accessType, fallback }: AccessTypePillProps) => {
  if (!accessType) return <>{fallback ?? <EmptyCell />}</>;

  return (
    <span className={cellStyles.statusPill} data-access-type={accessType}>
      {accessType}
    </span>
  );
};

/* ============ ReceiptCell ============ */

const RECEIPT_AVAILABLE_SVG = (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 31 30" fill="#34A853">
    <path d="M27.8549 13.4252L26.1549 11.4502C25.8299 11.0752 25.5674 10.3752 25.5674 9.8752V7.7502C25.5674 6.4252 24.4799 5.3377 23.1549 5.3377H21.0299C20.5424 5.3377 19.8299 5.0752 19.4549 4.7502L17.4799 3.0502C16.6174 2.3127 15.2049 2.3127 14.3299 3.0502L12.3674 4.7627C11.9924 5.0752 11.2799 5.3377 10.7924 5.3377H8.62988C7.30488 5.3377 6.21738 6.4252 6.21738 7.7502V9.8877C6.21738 10.3752 5.95488 11.0752 5.64238 11.4502L3.95488 13.4377C3.22988 14.3002 3.22988 15.7002 3.95488 16.5627L5.64238 18.5502C5.95488 18.9252 6.21738 19.6252 6.21738 20.1127V22.2502C6.21738 23.5752 7.30488 24.6627 8.62988 24.6627H10.7924C11.2799 24.6627 11.9924 24.9252 12.3674 25.2502L14.3424 26.9502C15.2049 27.6877 16.6174 27.6877 17.4924 26.9502L19.4674 25.2502C19.8424 24.9252 20.5424 24.6627 21.0424 24.6627H23.1674C24.4924 24.6627 25.5799 23.5752 25.5799 22.2502V20.1252C25.5799 19.6377 25.8424 18.9252 26.1674 18.5502L27.8674 16.5752C28.5924 15.7127 28.5924 14.2877 27.8549 13.4252ZM21.1049 12.6377L15.0674 18.6752C14.8924 18.8502 14.6549 18.9502 14.4049 18.9502C14.1549 18.9502 13.9174 18.8502 13.7424 18.6752L10.7174 15.6502C10.3549 15.2877 10.3549 14.6877 10.7174 14.3252C11.0799 13.9627 11.6799 13.9627 12.0424 14.3252L14.4049 16.6877L19.7799 11.3127C20.1424 10.9502 20.7424 10.9502 21.1049 11.3127C21.4674 11.6752 21.4674 12.2752 21.1049 12.6377Z" />
  </svg>
);

const RECEIPT_MISSING_SVG = (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 31 30" fill="#EE712B">
    <path d="M27.8549 13.4246L26.1674 11.4496C25.8549 11.0746 25.5924 10.3746 25.5924 9.87461V7.74961C25.5924 6.42461 24.5049 5.33711 23.1799 5.33711H21.0549C20.5549 5.33711 19.8424 5.07461 19.4674 4.76211L17.4924 3.07461C16.6299 2.33711 15.2174 2.33711 14.3549 3.07461L12.3549 4.76211C11.9799 5.07461 11.2799 5.33711 10.7799 5.33711H8.61738C7.29238 5.33711 6.20488 6.42461 6.20488 7.74961V9.87461C6.20488 10.3621 5.95488 11.0621 5.64238 11.4371L3.95488 13.4246C3.22988 14.2996 3.22988 15.6996 3.95488 16.5496L5.64238 18.5371C5.95488 18.8996 6.20488 19.6121 6.20488 20.0996V22.2371C6.20488 23.5621 7.29238 24.6496 8.61738 24.6496H10.7924C11.2799 24.6496 11.9924 24.9121 12.3674 25.2246L14.3424 26.9121C15.2049 27.6496 16.6174 27.6496 17.4799 26.9121L19.4549 25.2246C19.8299 24.9121 20.5299 24.6496 21.0299 24.6496H23.1549C24.4799 24.6496 25.5674 23.5621 25.5674 22.2371V20.1121C25.5674 19.6121 25.8299 18.9121 26.1424 18.5371L27.8299 16.5621C28.5924 15.7121 28.5924 14.2996 27.8549 13.4246ZM14.9674 10.1621C14.9674 9.64961 15.3924 9.22461 15.9049 9.22461C16.4174 9.22461 16.8424 9.64961 16.8424 10.1621V16.1996C16.8424 16.7121 16.4174 17.1371 15.9049 17.1371C15.3924 17.1371 14.9674 16.7121 14.9674 16.1996V10.1621ZM15.9049 21.0871C15.2174 21.0871 14.6549 20.5246 14.6549 19.8371C14.6549 19.1496 15.2049 18.5871 15.9049 18.5871C16.5924 18.5871 17.1549 19.1496 17.1549 19.8371C17.1549 20.5246 16.6049 21.0871 15.9049 21.0871Z" />
  </svg>
);

type ReceiptCellProps = {
  receiptAvailable: boolean;
  activityStatus: string;
  activityName: string;
};

export const ReceiptCell = ({ receiptAvailable, activityStatus, activityName }: ReceiptCellProps) => {
  const isNovattiCardTxn =
    (activityStatus === "SETTLED" || activityStatus === "APPROVED") && activityName === "NOVATTI_CARD_TRANSACTION";

  if (receiptAvailable) return <>{RECEIPT_AVAILABLE_SVG}</>;
  if (isNovattiCardTxn) return <>{RECEIPT_MISSING_SVG}</>;

  return <span>N/A</span>;
};
